import type { AppRouter } from "@klarify/api";
import { isAbortError } from "@klarify/utilities";
import type { Operation, TRPCClientError } from "@trpc/client";

/** Retry budget after the initial attempt. */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Decides whether a failed operation may be replayed.
 *
 * Retries exist for one reason: recovering from an expired Clerk session token
 * by refreshing the JWT and replaying the request.
 *
 * Mutations are never retried. A transport failure carries no information about
 * whether the server already committed the write, so replaying one duplicates
 * it — `client.addByTherapist` mints a second client row on every replay,
 * because email-less clients get a freshly generated `@anonymous` address that
 * no unique index can collide with.
 *
 * Queries stay lenient: a shapeless error (no `error.data`) is a transport-level
 * failure — dropped connection, timeout, or a proxy 5xx that never reached the
 * tRPC handler — and a 401 from the edge arrives exactly that way. That is the
 * case token refresh is meant to fix, and replaying a read is harmless.
 *
 * Aborts are the exception to that leniency. They are also shapeless, but an
 * aborted request was cancelled on purpose — by an unmount, or by the transport
 * tearing a finished stream down — so a token refresh cannot help and replaying
 * it just burns three more requests plus a `getToken({ skipCache: true })` round
 * trip against Clerk.
 */

export function shouldRetryOperation({
	op,
	error,
	attempts,
}: {
	op: Pick<Operation, "type">;
	error: TRPCClientError<AppRouter>;
	attempts: number;
}): boolean {
	if (op.type !== "query") {
		return false;
	}
	if (isAbortError(error) || isAbortError(error.cause)) {
		return false;
	}
	if (error.data && error.data.code !== "UNAUTHORIZED") {
		return false;
	}
	return attempts <= MAX_RETRY_ATTEMPTS;
}
